// Compose, pure parts: reading typed addresses into chips (the same one-plain-mailbox rule the
// server applies), the patch an autosave sends (only what changed), the words for a draft's
// state (CLAUDE.md invariant 8: calm, never "overdue"), file sizes against Gmail's limit, and the
// default for "remind me if they don't reply". Nothing here talks to the server.
import type {
  Address,
  ComposeDraftPatch,
  ComposeDraftView,
  ComposePreview,
  ComposeRemindDays,
  WorkView,
} from "@shared/api";
import { placeholderProblem } from "@shared/snippets";
import { sendAtWords } from "@web/sendLater/when";

/** One plain mailbox, as server/gateway/from.ts accepts it (lowercased before the test). */
const MAILBOX =
  /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

export const MAX_RECIPIENTS = 50;

export function isMailbox(email: string): boolean {
  const value = email.trim().toLowerCase();
  return value.length <= 320 && MAILBOX.test(value);
}

/** The address a chip carries: lowercased mailbox, trimmed name (quotes and angle brackets off). */
export function normalizeAddress(a: Address): Address {
  const email = a.email.trim().toLowerCase();
  const name = a.name
    ?.trim()
    .replace(/^["']|["']$/g, "")
    .trim();
  return name ? { name, email } : { email };
}

/** One typed entry: "Sam Lee <sam@lumen.example>", "<sam@…>", "sam@…" or "sam@… (Sam Lee)". */
export function parseRecipient(text: string): Address | null {
  const raw = text.trim();
  if (!raw) return null;
  const angled = /^(.*?)\s*<([^<>]+)>\s*$/.exec(raw);
  if (angled) return normalizeAddress({ name: angled[1] ?? "", email: angled[2] ?? "" });
  const parenthesised = /^(\S+)\s*\(([^()]+)\)$/.exec(raw);
  if (parenthesised)
    return normalizeAddress({ name: parenthesised[2] ?? "", email: parenthesised[1] ?? "" });
  return normalizeAddress({ email: raw });
}

/**
 * Everything typed or pasted into a recipients field, split on commas, semicolons and line
 * breaks (and on spaces between bare addresses), each entry kept even when it is not a valid
 * mailbox so the reader sees what to fix.
 */
export function parseRecipients(text: string): Address[] {
  const parts = text
    .split(/[,;\n]+/)
    .flatMap((part) => (part.includes("<") || part.includes("(") ? [part] : part.split(/\s+/)))
    .map((p) => p.trim())
    .filter(Boolean);
  const out: Address[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const a = parseRecipient(part);
    if (!a || seen.has(a.email)) continue;
    seen.add(a.email);
    out.push(a);
  }
  return out;
}

export interface DraftFields {
  fromEmail: string;
  to: Address[];
  cc: Address[];
  bcc: Address[];
  subject: string;
  /** The editor's rich text (the server sanitizes it and derives the plain text). */
  bodyHtml: string;
  includeSignature: boolean;
}

export function fieldsOf(draft: ComposeDraftView): DraftFields {
  return {
    fromEmail: draft.fromEmail,
    to: draft.to,
    cc: draft.cc,
    bcc: draft.bcc,
    subject: draft.subject,
    bodyHtml: draft.bodyHtml,
    includeSignature: draft.includeSignature,
  };
}

export const EMPTY_FIELDS: DraftFields = {
  fromEmail: "",
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  bodyHtml: "",
  includeSignature: true,
};

/** The visible text of rich text, roughly (tags dropped, common entities read). */
export function htmlText(html: string): string {
  return html
    .replace(/<br\s*\/?>|<\/(p|li|blockquote|div)>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const sameList = (a: readonly Address[], b: readonly Address[]) =>
  a.length === b.length &&
  a.every((x, i) => x.email === b[i]?.email && (x.name ?? "") === (b[i]?.name ?? ""));

export function sameFields(a: DraftFields, b: DraftFields): boolean {
  return (
    a.fromEmail === b.fromEmail &&
    a.subject === b.subject &&
    a.bodyHtml === b.bodyHtml &&
    a.includeSignature === b.includeSignature &&
    sameList(a.to, b.to) &&
    sameList(a.cc, b.cc) &&
    sameList(a.bcc, b.bcc)
  );
}

/** The fields that differ from `base` (what the last save or load returned), for one PATCH. */
export function draftPatch(base: DraftFields, next: DraftFields): ComposeDraftPatch {
  const patch: ComposeDraftPatch = {};
  if (next.fromEmail !== base.fromEmail) patch.fromEmail = next.fromEmail;
  if (!sameList(next.to, base.to)) patch.to = next.to;
  if (!sameList(next.cc, base.cc)) patch.cc = next.cc;
  if (!sameList(next.bcc, base.bcc)) patch.bcc = next.bcc;
  if (next.subject !== base.subject) patch.subject = next.subject;
  if (next.bodyHtml !== base.bodyHtml) patch.bodyHtml = next.bodyHtml;
  if (next.includeSignature !== base.includeSignature)
    patch.includeSignature = next.includeSignature;
  return patch;
}

export function hasContent(fields: DraftFields): boolean {
  return (
    fields.to.length > 0 ||
    fields.cc.length > 0 ||
    fields.bcc.length > 0 ||
    fields.subject.trim().length > 0 ||
    htmlText(fields.bodyHtml).length > 0
  );
}

/** Addresses (lowercased) in the fields that are not one plain mailbox. */
export function invalidRecipients(fields: Pick<DraftFields, "to" | "cc" | "bcc">): string[] {
  return [...fields.to, ...fields.cc, ...fields.bcc]
    .map((a) => a.email)
    .filter((email) => !isMailbox(email));
}

/** Why the draft cannot be sent yet, in one line; null when it can. */
export function sendProblem(fields: DraftFields): string | null {
  const bad = invalidRecipients(fields);
  if (bad.length === 1) return `${bad[0]} doesn't look like an email address.`;
  if (bad.length > 1) return `${bad.length} addresses don't look like email addresses.`;
  const count = fields.to.length + fields.cc.length + fields.bcc.length;
  if (count === 0) return "Add at least one person to send to.";
  if (count > MAX_RECIPIENTS) return `At most ${MAX_RECIPIENTS} people per message.`;
  if (!fields.fromEmail) return "Choose which address to send from.";
  // A snippet's [[fill: …]] never goes out (the server refuses it too).
  return placeholderProblem([fields.subject, htmlText(fields.bodyHtml)]);
}

/** Whole seconds left in an undo window that ends at `until`, or 0 once it has passed. */
export function undoSecondsLeft(until: string | null | undefined, now: number): number {
  if (!until) return 0;
  const end = new Date(until).getTime();
  if (Number.isNaN(end)) return 0;
  return Math.max(0, Math.ceil((end - now) / 1000));
}

export type DraftTone = "plain" | "note" | "calm";

export interface DraftStateWords {
  /** Short state for a list row or a kicker: "Draft", "Waiting to send", "Sent". */
  label: string;
  /** One line with detail: "Waiting to send · 8 s", "Didn't send · try again". */
  line: string;
  tone: DraftTone;
}

function firstSentence(text: string | null | undefined): string {
  const t = (text ?? "").trim();
  if (!t) return "";
  // The gateway's errors read "reason: what happened"; the reader needs the second part.
  const parts = t.split(": ");
  const words = parts.length > 1 ? parts.slice(1).join(": ") : t;
  return words.length > 140 ? `${words.slice(0, 137)}…` : words;
}

/** What a draft is doing, in words a person would use (`timeZone`: the owner's clock). */
export function draftStateWords(
  draft: Pick<ComposeDraftView, "state" | "work" | "undoUntil" | "scheduledFor">,
  now: number,
  timeZone = "UTC",
): DraftStateWords {
  const work: WorkView | null = draft.work;
  switch (draft.state) {
    case "draft":
      // U12: a scheduled send that met a new message came back with the reason.
      if (work?.status === "stale" && work.scheduledFor && work.resultText)
        return { label: "Back for you", line: work.resultText, tone: "note" };
      return { label: "Draft", line: "Draft", tone: "plain" };
    case "sent":
      return { label: "Sent", line: "Sent", tone: "calm" };
    case "discarded":
      return { label: "Discarded", line: "Discarded", tone: "plain" };
    case "failed": {
      const why = firstSentence(work?.error);
      return {
        label: "Didn't send",
        line: why ? `Didn't send · ${why}` : "Didn't send · try again",
        tone: "note",
      };
    }
    case "approved":
    default: {
      if (draft.scheduledFor)
        return {
          label: "Scheduled",
          line: `Sends ${sendAtWords(draft.scheduledFor, now, timeZone)}`,
          tone: "plain",
        };
      const left = undoSecondsLeft(draft.undoUntil, now);
      if (left > 0)
        return { label: "Waiting to send", line: `Waiting to send · ${left} s`, tone: "plain" };
      if (work?.phase === "restore_quarantine")
        return {
          label: "Paused",
          line: "Paused after a restore · check Gmail, or choose not to send",
          tone: "note",
        };
      if (work?.phase === "main_unknown")
        return {
          label: "Outcome unknown",
          line: "Outcome unknown · checking Gmail, never sent twice",
          tone: "note",
        };
      if (work?.status === "blocked")
        return {
          label: "Needs a decision",
          line: `Needs a decision · ${firstSentence(work.error) || "see the work item"}`,
          tone: "note",
        };
      return { label: "Sending", line: "Sending…", tone: "plain" };
    }
  }
}

/** "Omar Haddad, priya@… +1" for a list row. */
export function recipientSummary(
  draft: Pick<ComposeDraftView, "to" | "cc" | "bcc">,
  max = 2,
): string {
  const all = [...draft.to, ...draft.cc, ...draft.bcc];
  if (!all.length) return "No one yet";
  const names = all.map((a) => a.name?.trim() || a.email);
  return names.length <= max
    ? names.join(", ")
    : `${names.slice(0, max).join(", ")} +${names.length - max}`;
}

export function draftTitle(draft: Pick<ComposeDraftView, "subject" | "replyToThreadKey">): string {
  const subject = draft.subject.trim();
  if (subject) return subject;
  return draft.replyToThreadKey ? "Reply (subject from the thread)" : "(no subject)";
}

// ---------------------------------------------------------------------------------------------
// Attachments and reminders

/** Gmail's limit on a message's attachments (the server checks it too). */
export const MAX_ATTACHMENT_BYTES = 25_000_000;

/** "340 KB", "4.2 MB": sizes as people read them (decimal, like Gmail's limit). */
export function formatBytes(bytes: number): string {
  if (bytes < 1_000) return `${Math.max(bytes, 0)} bytes`;
  if (bytes < 1_000_000) return `${Math.round(bytes / 1_000)} KB`;
  return `${(bytes / 1_000_000).toFixed(bytes < 10_000_000 ? 1 : 0)} MB`;
}

/** Bytes on the draft that will be sent (left-out files do not count). */
export function attachedBytes(draft: Pick<ComposeDraftView, "attachments"> | null): number {
  return (draft?.attachments ?? []).filter((a) => a.included).reduce((n, a) => n + a.size, 0);
}

/** The files of `adding` that fit under the limit after what is attached, and those that don't. */
export function fitFiles<T extends { size: number }>(
  attached: number,
  adding: readonly T[],
): { fit: T[]; tooBig: T[] } {
  const fit: T[] = [];
  const tooBig: T[] = [];
  let total = attached;
  for (const file of adding) {
    if (total + file.size > MAX_ATTACHMENT_BYTES) tooBig.push(file);
    else {
      fit.push(file);
      total += file.size;
    }
  }
  return { fit, tooBig };
}

export const REMIND_CHOICES: readonly Exclude<ComposeRemindDays, 0>[] = [2, 3, 5, 7];
export const DEFAULT_REMIND_DAYS: Exclude<ComposeRemindDays, 0> = 3;

/**
 * "Remind me if they don't reply" starts on for a reply to one to five people (a conversation
 * where an answer is owed); otherwise it starts off. The owner can always switch it.
 */
export function remindByDefault(
  preview: Pick<ComposePreview, "threadId" | "to" | "cc" | "bcc">,
): boolean {
  const people = preview.to.length + preview.cc.length + preview.bcc.length;
  return preview.threadId !== null && people >= 1 && people <= 5;
}

/** The text the owner wrote, without the signature appended after "-- ". */
function ownText(p: Pick<ComposePreview, "bodyText" | "signature">): string {
  if (!p.signature) return p.bodyText;
  const at = p.bodyText.lastIndexOf("-- \n");
  return at < 0 ? p.bodyText : p.bodyText.slice(0, at).trimEnd();
}

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && [...a].sort().join("\n") === [...b].sort().join("\n");

/**
 * What differs between the review the owner looked at and the message as it stands now, in
 * plain words ("a new message arrived in the conversation", "your signature"). Empty when the
 * difference is nothing a person would see.
 */
export function reviewChanges(before: ComposePreview, after: ComposePreview): string[] {
  const out: string[] = [];
  if (before.parentMessageId !== after.parentMessageId)
    out.push("a new message arrived in the conversation");
  if (
    before.from.address !== after.from.address ||
    before.from.displayName !== after.from.displayName
  )
    out.push("who it's from");
  if (
    !sameSet(before.to, after.to) ||
    !sameSet(before.cc, after.cc) ||
    !sameSet(before.bcc, after.bcc)
  )
    out.push("who it goes to");
  if (before.subject !== after.subject) out.push("the subject");
  if (ownText(before) !== ownText(after)) out.push("the message text");
  if (
    before.signature !== after.signature ||
    before.signatureText !== after.signatureText ||
    !sameSet(before.signatureImageHosts, after.signatureImageHosts)
  )
    out.push("your signature");
  const files = (p: ComposePreview) => p.attachments.map((a) => `${a.name}:${a.size}`);
  if (files(before).join("\n") !== files(after).join("\n")) out.push("the files");
  if (!out.length && before.fingerprint !== after.fingerprint) out.push("the message's formatting");
  return out;
}
